import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { NavigateFunction } from "react-router-dom";
import {
  CustomLabel,
  FlexContainer,
  Input,
  IOption,
  Loader,
  ModalComponent,
  RedButton,
  SapphireBlueButton,
  SingleSelect,
  theme,
  ToastService,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { firstValueFrom } from "rxjs";
import { TaxStatus } from "../enums";
import { CountryWithStateModel, CreateContactModel } from "../models";
import { AuthService, ContactService, CountryService } from "../services";
import { InvoicePopupWrapper } from "../styles";
import CountryPhoneDropdown from "./CountryPhoneDropdown";
import PlusIcon from "../../../../../images/products/plus.svg";
import CountryDropdown from "./CountryDropdown";

type IProps = {
  navigate: NavigateFunction;
  isEdit?: boolean;
  contactId?: string;
  onClose: (refresh: boolean, contactId?: string) => void;
};
const taxStatusOptions: IOption[] = Object.keys(TaxStatus).map((status) => ({
  label: TaxStatus[status],
  value: status,
}));

const NewContactPopup: React.FC<IProps> = ({ onClose, isEdit, contactId }) => {
  const [isAPIRequestPending, setAPIRequestPending] = React.useState(false);
  const [showAdditionalFields, setShowAdditionalFields] = React.useState(false);
  const [data, setData] = useState<CreateContactModel>(
    new CreateContactModel()
  );
  const [showLoading, setShowLoading] = useState(false);
  const countryStates = (data.countryDetails?.states || [])
    .map((state) => {
      return {
        label: state.name,
        value: state.id,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  useEffect(() => {
    if (isEdit) {
      loadContactsData().then(() => {
        loadCountriesData();
      });
    } else {
      loadCountriesData();
    }
  }, [isEdit]);

  const loadCountriesData = async () => {
    setShowLoading(true);
    const response = await firstValueFrom(
      CountryService.getCountriesWithState()
    );
    setData((prevData) => {
      const defaultUS = response.result.find(
        (c) => c.abbreviationName === "US"
      );

      return new CreateContactModel({
        ...prevData,
        countries: response.result.map((c) => new CountryWithStateModel(c)),
        mobilePhoneCountry:
          response.result.find(
            (c) => c.id === prevData.mobilePhoneCountry?.id
          ) || defaultUS,
        homePhoneCountry:
          response.result.find((c) => c.id === prevData.homePhoneCountry?.id) ||
          defaultUS,
        workPhoneCountry:
          response.result.find((c) => c.id === prevData.workPhoneCountry?.id) ||
          defaultUS,
        countryDetails:
          response.result.find((c) => c.id === prevData.countryDetails?.id) ||
          defaultUS,
        taxCountryDetails:
          response.result.find(
            (c) => c.id === prevData.taxCountryDetails?.id
          ) || defaultUS,
      });
    });
    setShowLoading(false);
  };

  const loadContactsData = async () => {
    setShowLoading(true);
    const response = await ContactService.getContact(
      AuthService.authData.accountId,
      contactId || ""
    ).toPromise();

    if (response?.result) {
      const details = CreateContactModel.deserializeAPIModel(
        response.result.detail
      );
      setData(details);
    } else {
      ToastService.showError("Error in getting contact details");
    }
  };

  function row(label: string, rightSide: React.ReactNode) {
    return (
      <FlexContainer spacing={50} alignItems="center">
        <CustomLabel textAlign="right">{label}</CustomLabel>
        <div>
          <FlexContainer alignItems="center" spacing={20}>
            {rightSide}
          </FlexContainer>
        </div>
      </FlexContainer>
    );
  }

  const handleInputChange = (
    field: keyof CreateContactModel,
    value: string | number | boolean | Date | CountryWithStateModel
  ) => {
    setData(
      (prevData) => new CreateContactModel({ ...prevData, [field]: value })
    );
  };

  const singleSelect = (
    field: keyof CreateContactModel,
    selectedKey: string,
    className: string,
    options: IOption[],
    error: boolean,
    errorMessage: string,
    searchable: boolean
  ): React.ReactNode => {
    return (
      <SingleSelect
        selectedKey={selectedKey}
        hasError={error}
        searchable={searchable}
        nameKeys={["label"]}
        valueKeys={["value"]}
        options={[options || []]}
        errorMessage={errorMessage}
        onChange={(option: any) => handleInputChange(field, option.value)}
        className={`${className} dpdn-no-separator`}
      />
    );
  };

  const textInput = (
    field: keyof CreateContactModel,
    type: string,
    value: string | Date,
    className: string,
    error: boolean,
    placeholder: string
  ) => {
    return (
      <Input
        type={type as "text" | "textarea" | "number" | "date"}
        value={value}
        error={error}
        errorMessage={""}
        className={`styled-input merchant-inputs ${className}`}
        onChange={(e) => handleInputChange(field, e.target.value)}
        placeholder={placeholder}
        onDateChange={(date) => handleInputChange(field, new Date(date))}
      />
    );
  };

  const handleCountryChange = (id: string, field: keyof CreateContactModel) => {
    if (id) {
      const country = data.countries.find(
        (country: CountryWithStateModel) => country.id === id
      );
      if (country) {
        handleInputChange(field, country);
      }
    }
  };

  const renderAdditionalFields = () => {
    return (
      <>
        {row(
          "Work Phone",
          <FlexContainer>
            <CountryPhoneDropdown
              selectedCountry={data.workPhoneCountry}
              error={
                data.hasError("workPhoneCountry") ||
                data.hasError("workPhoneNumber")
              }
              errorMessage={"Please select country and enter phone number"}
              onCountryChange={(id: string) =>
                handleCountryChange(id, "workPhoneCountry")
              }
              phoneNumber={data.workPhoneNumber}
              onPhoneNumberChange={(number: string) =>
                handleInputChange("workPhoneNumber", number)
              }
              availableCountries={data.countries}
            />
          </FlexContainer>
        )}
        {row(
          "Home Phone",
          <FlexContainer>
            <CountryPhoneDropdown
              selectedCountry={data.homePhoneCountry}
              error={
                data.hasError("homePhoneCountry") ||
                data.hasError("homePhoneNumber")
              }
              errorMessage={"Please select country and enter phone number"}
              onCountryChange={(id: string) =>
                handleCountryChange(id, "homePhoneCountry")
              }
              phoneNumber={data.homePhoneNumber}
              onPhoneNumberChange={(number: string) =>
                handleInputChange("homePhoneNumber", number)
              }
              availableCountries={data.countries}
            />
          </FlexContainer>
        )}
        {row(
          "Address 1",
          <FlexContainer>
            {textInput(
              "address1",
              "text",
              data.address1,
              "",
              data.hasError("address1"),
              ""
            )}
          </FlexContainer>
        )}
        {row(
          "Address 2",
          <FlexContainer>
            {textInput(
              "address2",
              "text",
              data.address2,
              "",
              data.hasError("address2"),
              ""
            )}
          </FlexContainer>
        )}
        {row(
          "City",
          <FlexContainer>
            {textInput(
              "city",
              "text",
              data.city,
              "",
              data.hasError("city"),
              ""
            )}
          </FlexContainer>
        )}
        {row(
          "State",
          <FlexContainer>
            {singleSelect(
              "state",
              data.state,
              "default-padding-search-dpdn",
              countryStates,
              data.hasError("state"),
              "",
              true
            )}
          </FlexContainer>
        )}
        {row(
          "Zip",
          <FlexContainer>
            {textInput("zip", "text", data.zip, "", data.hasError("zip"), "")}
          </FlexContainer>
        )}
        {row(
          "Tax Status",
          <FlexContainer>
            {singleSelect(
              "taxStatus",
              data.taxStatus,
              "",
              taxStatusOptions,
              data.hasError("taxStatus"),
              "",
              false
            )}
          </FlexContainer>
        )}
        {row(
          "Tax ID",
          <FlexContainer alignItems="center" spacing={20}>
            <CountryDropdown
              selectedCountry={data.taxCountryDetails}
              error={data.hasError("taxCountryDetails")}
              errorMessage={"Please select country"}
              onCountryChange={(id: string) =>
                handleCountryChange(id, "taxCountryDetails")
              }
              availableCountries={data.countries}
            />
            {textInput(
              "taxId",
              "text",
              data.taxId,
              "",
              data.hasError("taxId"),
              ""
            )}
          </FlexContainer>
        )}
      </>
    );
  };

  const body = () => {
    return (
      <InvoicePopupWrapper className="new-contact-popup">
        {row(
          "First Name",
          <>
            {textInput(
              "firstName",
              "text",
              data.firstName,
              "",
              data.hasError("firstName"),
              ""
            )}
          </>
        )}
        {row(
          "Last Name",
          <FlexContainer>
            {textInput(
              "lastName",
              "text",
              data.lastName,
              "",
              data.hasError("lastName"),
              ""
            )}
          </FlexContainer>
        )}
        {row(
          "Email",
          <FlexContainer>
            {textInput(
              "email",
              "text",
              data.email,
              "",
              data.hasError("email"),
              ""
            )}
          </FlexContainer>
        )}
        {row(
          "Company",
          <FlexContainer>
            {textInput(
              "company",
              "text",
              data.company,
              "",
              data.hasError("company"),
              ""
            )}
          </FlexContainer>
        )}
        {row(
          "Mobile Phone",
          <FlexContainer>
            <CountryPhoneDropdown
              selectedCountry={data.mobilePhoneCountry}
              error={
                data.hasError("mobilePhoneCountry") ||
                data.hasError("mobilePhoneNumber")
              }
              errorMessage={
                data.errorMessage("mobilePhoneNumber") ||
                "Please select country and enter phone number"
              }
              onCountryChange={(id: string) =>
                handleCountryChange(id, "mobilePhoneCountry")
              }
              phoneNumber={data.mobilePhoneNumber}
              onPhoneNumberChange={(number: string) =>
                handleInputChange("mobilePhoneNumber", number)
              }
              availableCountries={data.countries}
            />
          </FlexContainer>
        )}
        {row(
          "Country",
          <FlexContainer>
            <CountryDropdown
              selectedCountry={data.countryDetails}
              error={data.hasError("countryDetails")}
              errorMessage={"Please select country"}
              onCountryChange={(id: string) =>
                handleCountryChange(id, "countryDetails")
              }
              availableCountries={data.countries}
            />
          </FlexContainer>
        )}
        {!showAdditionalFields && (
          <>
            {/* <img src={PlusIcon} alt="New" /> */}
            <CustomLabel
              // color="#556AB8"
              // textAlign="center"
              // fontWeight={'600'}
              className="more-fields-text"
              onClick={() => setShowAdditionalFields(!showAdditionalFields)}
            >
              More Information
            </CustomLabel>
          </>
        )}
        {showAdditionalFields && renderAdditionalFields()}
      </InvoicePopupWrapper>
    );
  };

  const onClickSave = () => {
    if (isAPIRequestPending) return;

    setAPIRequestPending(true);
    const isValidated = data.validate();

    if (isValidated) {
      const apiModelData = data.createAPIModel();
      if (isEdit) {
        ContactService.updateContact(apiModelData).subscribe({
          next: (response) => {
            if (response.result) {
              onClose(true, response.result.id);
              ToastService.showSuccess("Contact updated successfully");
            } else {
              ToastService.showError("Error in updating Contact");
            }
            setAPIRequestPending(false);
          },
          error: () => {
            setAPIRequestPending(false);
            ToastService.showError("Error in updating Contact");
          },
        });
        return;
      }
      ContactService.createContact(apiModelData).subscribe({
        next: (response) => {
          if (response.result?.id) {
            onClose(true, response.result.id);
            ToastService.showSuccess("Contact created successfully");
          } else {
            ToastService.showError("Error in creating Contact");
          }
          setAPIRequestPending(false);
        },
        error: () => {
          setAPIRequestPending(false);
          ToastService.showError("Error in creating Contact");
        },
      });
    } else {
      setAPIRequestPending(false);
      setData((prevData) => new CreateContactModel({ ...prevData }));
    }
  };

  return (
    <ModalComponent
      title={isEdit ? "Update Contact" : "New Contact"}
      titleFontSize="22px"
      bodyPadding="40px 110px 40px 40px"
      onClose={() => onClose && onClose(false)}
      showFooter={true}
      wrapperWidth="730px"
      bodyBackgroundColor="#fff"
      footerHeight="auto"
      footerContent={
        <FlexContainer alignItems="center" justifyContent="center" spacing={15}>
          <RedButton
            minWidth="120px"
            height="40px"
            onClick={() => onClose(false)}
          >
            Cancel
          </RedButton>
          <SapphireBlueButton
            primary
            disabled={isAPIRequestPending}
            minWidth="120px"
            height="40px"
            onClick={() => onClickSave()}
          >
            Save
          </SapphireBlueButton>
        </FlexContainer>
      }
    >
      {showLoading ? new Loader(true).loaderComponent : body()}
    </ModalComponent>
  );
};

export default withRouter(observer(NewContactPopup));
